import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

const PRIMARY = '#00C9A7';
const SECONDARY = '#FF4757';
const ACCENT = '#3742FA';

const fadeRange = (
  frame: number,
  inStart: number,
  inEnd: number,
  outStart: number,
  outEnd: number,
) => {
  const a = interpolate(frame, [inStart, inEnd], [0, 1], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  const b = interpolate(frame, [outStart, outEnd], [1, 0], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });
  return a * b;
};

const phaseOpacity = (frame: number, start: number, peakIn: number, peakOut: number, end: number) =>
  fadeRange(frame, start, peakIn, peakOut, end);

const HexBackdrop: React.FC<{ frame: number; opacity: number }> = ({ frame, opacity }) => (
  <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity }} xmlns="http://www.w3.org/2000/svg">
    <defs>
      <pattern id="hexPat" width="56" height="100" patternUnits="userSpaceOnUse" patternTransform={`translate(${frame % 56},0)`}>
        <path
          d="M28 0 L52 14 L52 42 L28 56 L4 42 L4 14 Z"
          fill="none"
          stroke="rgba(0,201,167,0.07)"
          strokeWidth="1"
        />
      </pattern>
    </defs>
    <rect width="1920" height="1080" fill="url(#hexPat)" />
  </svg>
);

const VirusParticle: React.FC<{
  cx: number;
  cy: number;
  r: number;
  frame: number;
  labelOpacity: number;
  gradId: string;
}> = ({
  cx,
  cy,
  r,
  frame,
  labelOpacity,
  gradId,
}) => {
  const spikes = 12;
  const rot = frame * 0.6;
  return (
    <g transform={`translate(${cx}, ${cy - 0}) rotate(${rot})`}>
      {Array.from({ length: spikes }, (_, i) => {
        const a = (i / spikes) * Math.PI * 2;
        const x2 = Math.cos(a) * (r + 50);
        const y2 = Math.sin(a) * (r + 50);
        return (
          <line
            key={i}
            x1={Math.cos(a) * r}
            y1={Math.sin(a) * r}
            x2={x2}
            y2={y2}
            stroke={SECONDARY}
            strokeWidth={8}
            strokeLinecap="round"
            opacity={0.85}
          />
        );
      })}
      <circle r={r} fill={`url(#${gradId})`} stroke={SECONDARY} strokeWidth={6} />
      <defs>
        <radialGradient id={gradId}>
          <stop offset="0%" stopColor="#ff6b7a" />
          <stop offset="100%" stopColor={SECONDARY} />
        </radialGradient>
      </defs>
      <g transform={`rotate(${-rot})`} opacity={labelOpacity}>
        <text x={0} y={-r - 95} textAnchor="middle" fill={ACCENT} fontSize="38" fontWeight="700">
          抗原蛋白
        </text>
        <text x={r + 80} y={-r - 30} fill={PRIMARY} fontSize="30" fontWeight="600">
          刺突
        </text>
        <line
          x1={r + 50}
          y1={-r + 10}
          x2={r + 78}
          y2={-r - 15}
          stroke={PRIMARY}
          strokeWidth={2}
          opacity={0.6}
        />
      </g>
    </g>
  );
};

const AntibodyShape: React.FC<{
  x: number;
  y: number;
  scale: number;
  rot: number;
  opacity: number;
}> = ({ x, y, scale, rot, opacity }) => (
  <g transform={`translate(${x},${y}) rotate(${rot}) scale(${scale})`} opacity={opacity}>
    <path
      d="M 0 -39 L -25 11 L 0 31 L 25 11 Z"
      fill="none"
      stroke={ACCENT}
      strokeWidth={5}
      strokeLinejoin="round"
    />
    <line x1={0} y1={31} x2={0} y2={59} stroke={ACCENT} strokeWidth={5} strokeLinecap="round" />
    <line x1={-20} y1={11} x2={-45} y2={-11} stroke={PRIMARY} strokeWidth={4} strokeLinecap="round" />
    <line x1={20} y1={11} x2={45} y2={-11} stroke={PRIMARY} strokeWidth={4} strokeLinecap="round" />
  </g>
);

const ImmuneCell: React.FC<{
  cx: number;
  cy: number;
  r: number;
  color: string;
  pulse: number;
  label: string;
}> = ({ cx, cy, r, color, pulse, label }) => (
  <g>
    <circle cx={cx} cy={cy} r={r * pulse} fill={color} opacity={0.35} />
    <circle
      cx={cx}
      cy={cy}
      r={r}
      fill={`${color}33`}
      stroke={color}
      strokeWidth={4}
    />
    <text x={cx} y={cy + 7} textAnchor="middle" fill="#fff" fontSize="20" fontWeight="700">
      {label}
    </text>
  </g>
);

const SyringeGraphic: React.FC<{ frame: number; fps: number; opacity: number }> = ({ frame, fps, opacity }) => {
  const t = frame - 138;
  const plunge = spring({ frame: Math.max(0, t), fps, config: { damping: 10, stiffness: 160 } });
  const liquid = interpolate(plunge, [0, 1], [0, 1]);
  const yOff = interpolate(plunge, [0, 1], [0, 28]);

  return (
    <div style={{ position: 'absolute', left: 180, top: 200, opacity }}>
      <svg width="380" height="560" viewBox="0 0 280 420">
        <rect x={60} y={80} width={100} height={200} rx={8} fill="rgba(255,255,255,0.08)" stroke={PRIMARY} strokeWidth={3} />
        <rect
          x={68}
          y={260 - liquid * 160}
          width={84}
          height={12 + liquid * 160}
          fill={`url(#vaxLiq)`}
          opacity={0.9}
        />
        <defs>
          <linearGradient id="vaxLiq" x1="0%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" stopColor={PRIMARY} />
            <stop offset="100%" stopColor={ACCENT} />
          </linearGradient>
        </defs>
        <polygon points="110,280 130,320 90,320" fill="rgba(255,255,255,0.15)" stroke={PRIMARY} strokeWidth={2} />
        <rect x={95} y={40} width={30} height={50} rx={4} fill="rgba(255,255,255,0.2)" stroke={PRIMARY} strokeWidth={2} />
        <line x1={110} y1={320} x2={110} y2={360 + yOff} stroke={SECONDARY} strokeWidth={4} strokeLinecap="round" opacity={0.6} />
      </svg>
      <div
        style={{
          marginTop: -10,
          fontSize: 30,
          fontWeight: 700,
          color: PRIMARY,
          maxWidth: 400,
        }}
      >
        疫苗含灭活病原体、亚单位或 mRNA 指令——安全展示抗原特征
      </div>
    </div>
  );
};

const VirusAntigenScene: React.FC<{ frame: number; fps: number; opacity: number }> = ({ frame, fps, opacity }) => {
  const p = spring({ frame: frame - 60, fps, config: { damping: 14, stiffness: 120 } });
  const labOp = interpolate(frame, [85, 105], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  return (
    <div style={{ position: 'absolute', left: 440, top: 140, opacity: opacity * p }}>
      <svg width="1400" height="650" viewBox="0 0 1200 520">
        <VirusParticle cx={400} cy={260} r={125} frame={frame} labelOpacity={labOp} gradId="vx-hero" />
        <g opacity={labOp}>
          <ellipse cx={860} cy={160} rx={260} ry={56} fill="none" stroke={ACCENT} strokeWidth={3} strokeDasharray="6 8" />
          <text x={860} y={170} textAnchor="middle" fill="rgba(255,255,255,0.75)" fontSize="30" fontWeight="600">
            免疫系统识别的“分子指纹”
          </text>
        </g>
      </svg>
    </div>
  );
};

const RecognitionScene: React.FC<{ frame: number; fps: number; opacity: number }> = ({ frame, fps, opacity }) => {
  const p = spring({ frame: frame - 218, fps, config: { damping: 15, stiffness: 130 } });
  const approach = interpolate(frame, [220, 260], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
  const cellX = interpolate(approach, [0, 1], [920, 520]);

  return (
    <div style={{ position: 'absolute', inset: 0, opacity: opacity * p }}>
      <svg width="1920" height="800" style={{ position: 'absolute', top: 130 }}>
        <ImmuneCell cx={cellX} cy={320} r={64} color={ACCENT} pulse={1 + Math.sin(frame * 0.12) * 0.06} label="APC" />
        <g transform={`translate(380, 300)`}>
          <VirusParticle cx={0} cy={0} r={90} frame={frame} labelOpacity={0.7} gradId="vx-rec" />
        </g>
        <line
          x1={450}
          y1={320}
          x2={cellX - 64}
          y2={320}
          stroke={PRIMARY}
          strokeWidth={3}
          strokeDasharray="10 8"
          opacity={0.4 + approach * 0.45}
        />
      </svg>
      <div
        style={{
          position: 'absolute',
          right: 150,
          top: 200,
          width: 580,
          padding: 28,
          background: 'rgba(15,20,40,0.85)',
          borderRadius: 16,
          border: `1px solid ${ACCENT}44`,
        }}
      >
        <h3 style={{ margin: 0, color: PRIMARY, fontSize: 34, fontWeight: 800 }}>抗原呈递与识别</h3>
        <p style={{ margin: '14px 0 0', fontSize: 28, color: 'rgba(255,255,255,0.74)', lineHeight: 1.55 }}>
          抗原呈递细胞（APC）摄取、处理并展示抗原片段，激活特异性淋巴细胞，启动适应性免疫。
        </p>
      </div>
    </div>
  );
};

const AntibodyArmyScene: React.FC<{ frame: number; fps: number; opacity: number }> = ({ frame, fps, opacity }) => {
  const count = Math.min(24, Math.floor((frame - 300) / 4));
  const abs = React.useMemo(
    () =>
      Array.from({ length: 24 }, (_, i) => ({
        x: 320 + (i % 6) * 130 + (i % 3) * 10,
        y: 200 + Math.floor(i / 6) * 110 + (i % 5) * 8,
        rot: (i * 47) % 360,
        delay: i * 3,
      })),
    [],
  );

  return (
    <div style={{ position: 'absolute', left: 80, top: 120, opacity }}>
      <h3 style={{ color: ACCENT, fontSize: 40, fontWeight: 800, margin: '0 0 10px' }}>抗体大量产生</h3>
      <p style={{ fontSize: 26, color: 'rgba(255,255,255,0.65)', margin: '0 0 18px', maxWidth: 800 }}>
        B 细胞分化为浆细胞，分泌能与抗原高精度结合的 Y 形抗体，中和病原体并标记清除。
      </p>
      <svg width="1500" height="500" viewBox="0 0 1500 500">
        {abs.slice(0, count).map((a, i) => {
          const sp = spring({
            frame: frame - 305 - a.delay,
            fps,
            config: { damping: 12, stiffness: 200 },
          });
          return (
            <AntibodyShape
              key={i}
              x={a.x}
              y={a.y}
              scale={1.15}
              rot={a.rot}
              opacity={sp}
            />
          );
        })}
        <g transform="translate(980, 250)">
          <VirusParticle cx={0} cy={0} r={72} frame={frame} labelOpacity={0.5} gradId="vx-ab" />
        </g>
      </svg>
    </div>
  );
};

const MemoryCellScene: React.FC<{ frame: number; fps: number; opacity: number }> = ({ frame, fps, opacity }) => {
  const p = spring({ frame: frame - 388, fps, config: { damping: 14, stiffness: 100 } });
  const glow = 1 + Math.sin(frame * 0.1) * 0.08;

  return (
    <div style={{ position: 'absolute', left: '50%', top: 160, transform: 'translateX(-50%)', opacity: opacity * p }}>
      <svg width="1100" height="540" viewBox="0 0 1100 540">
        <defs>
          <filter id="memGlow">
            <feGaussianBlur stdDeviation="5" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
        {[0, 1, 2].map((i) => {
          const cx = 320 + i * 220;
          const cy = 240 + Math.sin(frame * 0.07 + i) * 14;
          return (
            <g key={i} filter="url(#memGlow)">
              <ImmuneCell
                cx={cx}
                cy={cy}
                r={68 * glow}
                color={PRIMARY}
                pulse={1}
                label="记忆细胞"
              />
            </g>
          );
        })}
        <text x={540} y={440} textAnchor="middle" fill="rgba(255,255,255,0.7)" fontSize="28" fontWeight="600">
          长期存留：再次遇敌时迅速扩增，反应更快更强
        </text>
      </svg>
    </div>
  );
};

const IMMUNE_TIMELINE = [
  { t: '抗原识别', sub: 'APC 处理呈递' },
  { t: '淋巴细胞激活', sub: '克隆扩增' },
  { t: '抗体分泌', sub: '体液免疫高峰' },
  { t: '记忆形成', sub: '长期监视' },
  { t: '再次应答', sub: '更快更强' },
];

const ImmuneTimelineStrip: React.FC<{ frame: number; fps: number; opacity: number }> = ({
  frame,
  fps,
  opacity,
}) => {
  const active = interpolate(frame, [210, 520], [0, IMMUNE_TIMELINE.length - 0.001], {
    extrapolateLeft: 'clamp',
    extrapolateRight: 'clamp',
  });

  return (
    <div
      style={{
        position: 'absolute',
        left: 120,
        right: 120,
        bottom: 110,
        opacity,
        pointerEvents: 'none',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'space-between',
          gap: 12,
          padding: '16px 20px',
          borderRadius: 16,
          background: 'rgba(8,12,24,0.88)',
          border: `1px solid ${PRIMARY}33`,
        }}
      >
        {IMMUNE_TIMELINE.map((step, i) => {
          const hi = active >= i ? 1 : 0;
          const sp = spring({
            frame: frame - 205 - i * 5,
            fps,
            config: { damping: 14, stiffness: 180 },
          });
          const lit = hi * sp;
          return (
            <div
              key={i}
              style={{
                flex: 1,
                textAlign: 'center',
                padding: '10px 8px',
                borderRadius: 12,
                background: lit > 0.5 ? `linear-gradient(180deg, ${PRIMARY}22, transparent)` : 'transparent',
                border: lit > 0.5 ? `1px solid ${PRIMARY}55` : '1px solid rgba(255,255,255,0.06)',
                opacity: 0.35 + lit * 0.65,
                transform: `translateY(${(1 - sp) * 8}px)`,
              }}
            >
              <div style={{ fontSize: 26, fontWeight: 800, color: lit > 0.5 ? PRIMARY : 'rgba(255,255,255,0.45)' }}>
                {step.t}
              </div>
              <div style={{ fontSize: 24, color: 'rgba(255,255,255,0.4)', marginTop: 5 }}>{step.sub}</div>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: 10, textAlign: 'center', fontSize: 26, color: 'rgba(255,255,255,0.28)' }}>
        免疫应答时间线（示意，个体差异大）
      </div>
    </div>
  );
};

const SecondaryResponseChart: React.FC<{ frame: number; fps: number; opacity: number }> = ({
  frame,
  fps,
  opacity,
}) => {
  const pr = spring({ frame: frame - 458, fps, config: { damping: 16, stiffness: 90 } });
  const w = 540;

  const primaryPath = (amp: number) => {
    const pts: string[] = [];
    for (let i = 0; i <= 50; i++) {
      const u = i / 50;
      const v = amp * Math.exp(-((u - 0.22) ** 2) / 0.02) + amp * 0.15 * Math.exp(-((u - 0.55) ** 2) / 0.08);
      pts.push(`${70 + u * w},${400 - v * pr}`);
    }
    return `M ${pts.join(' L ')}`;
  };

  return (
    <div style={{ position: 'absolute', right: 520, top: 110, opacity: opacity * pr, width: 820 }}>
      <h3 style={{ margin: '0 0 18px', color: PRIMARY, fontSize: 38, fontWeight: 800 }}>初次 vs 再次应答</h3>
      <svg width="720" height="460" viewBox="0 0 680 460">
        <rect x={60} y={50} width={w + 50} height={330} rx={10} fill="rgba(0,0,0,0.25)" stroke={PRIMARY} strokeOpacity="0.35" />
        <path d="M 60 380 L 650 380" stroke="rgba(255,255,255,0.2)" strokeWidth="2" />
        <path d={primaryPath(110)} fill="none" stroke={SECONDARY} strokeWidth={4} opacity={0.75} />
        <path d={primaryPath(200)} fill="none" stroke={PRIMARY} strokeWidth={5} />
        <text x={440} y={96} fill={SECONDARY} fontSize={22} fontWeight="700">
          初次（较慢、峰值低）
        </text>
        <text x={440} y={130} fill={PRIMARY} fontSize={22} fontWeight="700">
          再次（快、峰值高）
        </text>
        <text x={350} y={430} textAnchor="middle" fill="rgba(255,255,255,0.45)" fontSize={24}>
          时间
        </text>
        <text x={40} y={250} textAnchor="middle" fill="rgba(255,255,255,0.45)" fontSize={24} transform="rotate(-90 28 250)">
          抗体水平
        </text>
      </svg>
      <p style={{ fontSize: 26, color: 'rgba(255,255,255,0.68)', lineHeight: 1.5, marginTop: 14 }}>
        疫苗的意义正在于“预演”感染：在不致病的前提下建立记忆，从而在未来真实暴露时赢得时间窗口。
      </p>
    </div>
  );
};

export const VaccineMechanismAnimation: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const bgFade = spring({ frame, fps, config: { damping: 14, stiffness: 120 } });
  const titleP = spring({ frame: frame - 4, fps, config: { damping: 12, stiffness: 200 } });
  const titleY = interpolate(titleP, [0, 1], [-38, 0]);

  const opTitle = phaseOpacity(frame, 0, 8, 50, 68);
  const opVirus = phaseOpacity(frame, 55, 72, 128, 148);
  const opInject = phaseOpacity(frame, 135, 152, 210, 228);
  const opRecog = phaseOpacity(frame, 215, 232, 292, 310);
  const opAb = phaseOpacity(frame, 297, 314, 378, 396);
  const opMem = phaseOpacity(frame, 383, 400, 448, 466);
  const opCompare = phaseOpacity(frame, 453, 470, 515, 540);
  const opTimeline = phaseOpacity(frame, 198, 215, 528, 540);

  return (
    <AbsoluteFill
      style={{
        background: `radial-gradient(ellipse at 70% 20%, #0f1a22 0%, #0B1120 50%, #08090c 100%)`,
        fontFamily: 'system-ui, -apple-system, "PingFang SC", sans-serif',
        overflow: 'hidden',
      }}
    >
      <HexBackdrop frame={frame} opacity={bgFade * 0.9} />

      <div
        style={{
          position: 'absolute',
          top: 40,
          left: 56,
          opacity: opTitle * titleP,
          transform: `translateY(${titleY}px)`,
        }}
      >
        <h1
          style={{
            fontSize: 78,
            fontWeight: 900,
            margin: 0,
            background: `linear-gradient(120deg, ${PRIMARY}, ${ACCENT})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          疫苗工作原理
        </h1>
        <p style={{ fontSize: 36, margin: '12px 0 0', color: 'rgba(255,255,255,0.42)' }}>
          免疫系统的训练 · 抗原、抗体与免疫记忆
        </p>
      </div>

      <VirusAntigenScene frame={frame} fps={fps} opacity={opVirus} />
      <SyringeGraphic frame={frame} fps={fps} opacity={opInject} />
      <RecognitionScene frame={frame} fps={fps} opacity={opRecog} />
      <AntibodyArmyScene frame={frame} fps={fps} opacity={opAb} />
      <MemoryCellScene frame={frame} fps={fps} opacity={opMem} />
      <SecondaryResponseChart frame={frame} fps={fps} opacity={opCompare} />
      <ImmuneTimelineStrip frame={frame} fps={fps} opacity={opTimeline} />

      <div
        style={{
          position: 'absolute',
          bottom: 30,
          left: 56,
          fontSize: 30,
          color: 'rgba(255,255,255,0.26)',
        }}
      >
        硬核科普 · 通识教育 · 示意动画非临床建议
      </div>
    </AbsoluteFill>
  );
};
